import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/ui/buttons/Button';
import Pagination from '../../../../../../components/ui/pagination/Pagination';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import { LineChart, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../../../../../utils/formatters';
import CustomRoundedButton from '../../../../../../components/ui/buttons/CustomRoundedButton';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';
import { useConfirm } from '../../../../../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../../../../../utils/fetchWithCsrf';
import FinancialModal from '../../../../../../components/modals/properties/FinancialModal';
import SecondaryButton from '../../../../../../components/ui/buttons/SecondaryButton';
import ImportFinancialsModal from '../../../../../../components/modals/properties/ImportFinancialsModal';

const initialPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

const MODALITY_LABELS = {
  national: 'Nacional',
  international: 'Internacional',
};

const GRID_TEMPLATE_COLUMNS = '48px minmax(80px, 1fr) minmax(180px, 1.4fr) 120px minmax(140px, 1fr) 140px 146px';

const gridRowStyle = {
  display: 'grid',
  gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
  columnGap: '8px',
  alignItems: 'center',
  minWidth: '980px',
};

const cellStyle = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const FinancialTab = ({ clientId, scope, canWrite }) => {
  const [total, setTotal] = useState(0);
  const [financials, setFinancials] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [showCreateFinancialModal, setShowCreateFinancialModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFinancialId, setSelectedFinancialId] = useState(null);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData(1);
  }, [clientId, scope]);

  const buildQuery = (params) =>
    Object.entries(params)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const fetchData = (page = 1) => {
    const query = buildQuery({ page, scope });

    fetch(`/app/planner/client/${clientId}/dashboard/properties/financials?${query}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setFinancials(camelData.financials || []);
        setPagination(camelData.pagination || initialPagination);
        setTotal(camelData.total || 0);
      });
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'EXCLUIR REGISTRO?',
      description: 'Tem certeza que deseja excluir o registro?',
      confirmText: 'EXCLUIR',
    });

    if (!ok) return;

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/properties/financials/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao excluir o registro');
      }

      addToast({ type: 'success', message: 'Registro excluído com sucesso!' });
      fetchData(pagination.currentPage);
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleModalSuccess = () => {
    setShowCreateFinancialModal(false);
    setShowImportModal(false);
    setSelectedFinancialId(null);
    fetchData(pagination.currentPage);
  };

  const formatModality = (modality) => MODALITY_LABELS[modality] || modality || '—';

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Título */}
      <h2 className="text-xl text-blueDarker font-semibold">Lista de saldos e investimentos</h2>

      {financials.length > 0 ? (
        <div className="overflow-x-auto">
          {/* HEADER */}
          <div className="px-2">
            <div style={gridRowStyle} className="text-xs text-tealBase font-medium">
              <div />

              <div style={cellStyle}>Código de Negociação</div>

              <div style={cellStyle}>Nome</div>

              <div style={cellStyle}>Origem</div>

              <div style={cellStyle}>Estratégia</div>

              <div style={cellStyle}>Valor Total</div>

              <div />
            </div>
          </div>

          {/* LISTA */}
          <div className="mt-1 flex flex-col gap-3">
            {financials.map((item) => (
              <div key={item.id} className="bg-blueLight rounded-xl px-3 py-3">
                <div style={gridRowStyle} className="text-sm text-blueSteel">
                  {/* ÍCONE */}
                  <div className="flex justify-center">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-white">
                      <LineChart className="text-tealMedium" size={18} strokeWidth={2} />
                    </span>
                  </div>

                  {/* Código de Negociação */}
                  <div style={cellStyle} title={item.code || '—'}>
                    {item.code || '—'}
                  </div>

                  {/* Nome */}
                  <div style={cellStyle} className="text-blueDarker font-semibold text-base" title={item.cnpj || '—'}>
                    {item.cnpj || '—'}
                  </div>

                  {/* Modalidade */}
                  <div style={cellStyle} title={formatModality(item.modality)}>
                    {formatModality(item.modality)}
                  </div>

                  {/* Estratégia */}
                  <div style={cellStyle} title={item.financialType || '—'}>
                    {item.financialType || '—'}
                  </div>

                  {/* Valor Bruto */}
                  <div
                    style={cellStyle}
                    className="font-medium text-blueDarker"
                    title={formatCurrency(item.grossValue)}
                  >
                    {formatCurrency(item.grossValue)}
                  </div>

                  {/* Ações */}
                  <div className="flex items-center justify-end gap-4 pr-1">
                    {canWrite && (
                      <>
                        <button onClick={() => handleDelete(item.id)} title="Excluir" disabled={scope === 'couple'}>
                          <Trash2
                            className={scope === 'couple' ? 'cursor-not-allowed text-gray-400' : 'text-redBase'}
                            size={18}
                          />
                        </button>

                        <CustomRoundedButton
                          disabled={scope === 'couple'}
                          size="sm"
                          onClick={() => setSelectedFinancialId(item.id)}
                        >
                          EDITAR
                        </CustomRoundedButton>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <p>Ainda sem registros.</p>
      )}

      {/* Footer */}
      <div className="mt-auto flex justify-between items-center">
        <div className="flex font-medium items-center gap-2">
          <span className="text-sm text-tealBase">TOTAL: </span>
          <span className="text-xl blueDark">{formatCurrency(total)}</span>
        </div>

        <div className="flex gap-3">
          {canWrite && (
            <>
              <SecondaryButton disabled={scope === 'couple'} onClick={() => setShowImportModal(true)}>
                IMPORTAR PLANILHA
              </SecondaryButton>

              <Button disabled={scope === 'couple'} onClick={() => setShowCreateFinancialModal(true)}>
                ADICIONAR SALDO/INVESTIMENTO
              </Button>
            </>
          )}
        </div>
      </div>

      {showCreateFinancialModal && (
        <FinancialModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowCreateFinancialModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showImportModal && (
        <ImportFinancialsModal
          clientId={clientId}
          scope={scope}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {selectedFinancialId && (
        <FinancialModal
          clientId={clientId}
          financialId={selectedFinancialId}
          scope={scope}
          onClose={() => setSelectedFinancialId(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default FinancialTab;
